import type { AlgorithmType } from "../../redistribution/models/AlgorithmType.ts";
import type { DemandSourceType } from "../../redistribution/models/DemandSourceType.ts";
import type { IntermediateOrderSkuRow } from "../../redistribution/models/IntermediateOrderSkuRow.ts";
import type { PurchaseOrder } from "../../redistribution/models/PurchaseOrder.ts";
import type { Sku } from "../../redistribution/models/Sku.js";
import { getWarehouseKey } from "../../utils/warehouseKeys.ts";

export const DEFAULT_INTERMEDIATE_ORDER_PO_CUBE = 999_999;

export const DEFAULT_INTERMEDIATE_ORDER_DIVISIONS: Record<number, string> = {
  0: "SE",
  20: "SE",
  79: "SE",
  89: "SE",
  94: "SE",
  97: "SE",
  99: "SE",
};

function normalizeIsoDate(value: string | null | undefined): string {
  if (!value) {
    return new Date().toISOString();
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return new Date().toISOString();
  }

  return date.toISOString();
}

function firstNonEmptyString(
  ...values: (string | null | undefined)[]
): string | undefined {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }

  return undefined;
}

/**
 * Product Master key for a SKU line. Prefer the key returned by the API (read
 * straight from the PL record). Fall back to the Product Master format
 * `{vendor}A.{sku}` (e.g. CANYA.34240402) only when the API did not return one
 * (older stored procedure). Never `{vendor}.{sku}` — that form matches nothing
 * in FlowData or the PO Release forecast API.
 */
export function resolveIntermediateOrderPmKey(row: {
  // Optional at runtime: API builds predating the PMKey column omit the field entirely.
  pmKey?: string | null;
  vendorCode: string;
  skuNumber: string;
}): string {
  const provided = row.pmKey?.trim();
  if (provided) {
    return provided;
  }

  return `${row.vendorCode.trim().toUpperCase()}A.${row.skuNumber.trim()}`;
}

/**
 * Date an Intermediate Order's inventory is expected to land. Intermediate
 * Orders carry no explicit arrival date, so fall back through the dates that
 * bound the end of the order's lifecycle before the ones that bound its start.
 */
export function resolveIntermediateOrderArrivalDate(
  row: Pick<
    IntermediateOrderSkuRow,
    | "shipDateRangeEnd"
    | "lastDistributionDate"
    | "vendorCutDate"
    | "shipDateRangeStart"
  >
): string | undefined {
  return firstNonEmptyString(
    row.shipDateRangeEnd,
    row.lastDistributionDate,
    row.vendorCutDate,
    row.shipDateRangeStart
  );
}

function toSku(row: IntermediateOrderSkuRow): Sku {
  const quantity = row.totalUnits;
  const cubeVolume = row.totalCubicFeet;
  const cubeSize = quantity > 0 ? cubeVolume / quantity : cubeVolume;

  return {
    pmKey: resolveIntermediateOrderPmKey(row),
    skuNumber: row.skuNumber,
    description: row.productDescription,
    lineItemQuantity: quantity,
    quantityAvailable: row.currentStock,
    requiredFirstRange: 0,
    requiredPerMonth: 0,
    requiredPerRange: 0,
    roS30: row.roS30,
    roS60: row.roS60,
    roS90: row.roS90,
    dailyRateOfSale: 0,
    daysToStockout: 0,
    urgency: 0,
    forecastQuantityAvailable: row.forecastQuantityAvailable,
    theme: row.collection,
    cubeSize,
    pieceCount: quantity > 0 ? 1 : 0,
    color: row.color,
    family: row.collection,
    stockedThroughDate: "",
    netAvailable: 0,
    threshold: 0,
    difference: 0,
    adjustedForecast: 0,
  };
}

export function buildIntermediateOrderPurchaseOrders(
  rows: IntermediateOrderSkuRow[] | undefined,
  warehouseDivisions: Record<
    number,
    string
  > = DEFAULT_INTERMEDIATE_ORDER_DIVISIONS
): PurchaseOrder[] {
  if (!rows || rows.length === 0) {
    return [];
  }

  const byIntermediateOrder = new Map<string, IntermediateOrderSkuRow[]>();
  for (const row of rows) {
    const key = `${row.bulkOrderNumber}:${row.intermediatePoNumber}`;
    const existing = byIntermediateOrder.get(key) ?? [];
    existing.push(row);
    byIntermediateOrder.set(key, existing);
  }

  return [...byIntermediateOrder.values()]
    .map((group) => {
      const first = group[0];
      if (!first) {
        return null;
      }

      const skus = group.map((row) => toSku(row));
      const totalUnits = skus.reduce(
        (sum, sku) => sum + sku.lineItemQuantity,
        0
      );
      const cubeTotal = skus.reduce(
        (sum, sku) => sum + sku.lineItemQuantity * sku.cubeSize,
        0
      );
      const arrivalDate = normalizeIsoDate(
        resolveIntermediateOrderArrivalDate(first)
      );
      const shipDate = normalizeIsoDate(
        firstNonEmptyString(
          first.vendorCutDate,
          first.shipDateRangeStart,
          first.shipDateRangeEnd,
          first.lastDistributionDate
        )
      );
      const division =
        warehouseDivisions[first.warehouseNumber] ??
        DEFAULT_INTERMEDIATE_ORDER_DIVISIONS[first.warehouseNumber] ??
        "SE";

      const order: PurchaseOrder = {
        vendorCode: first.vendorCode,
        arrivalDate,
        shipDate,
        division,
        purchaseOrderNumber: first.intermediatePoNumber,
        warehouse: first.warehouseNumber,
        warehouseName: first.warehouseName,
        purchaseOrderStatus: first.distributionStatus || "Open",
        countUniqueSkus: new Set(skus.map((sku) => sku.skuNumber)).size,
        sumQuantities: totalUnits,
        cubeTotal,
        calcArrivalDate: arrivalDate,
        skus,
      };
      return order;
    })
    .filter((value): value is PurchaseOrder => value !== null)
    .toSorted((a, b) =>
      a.purchaseOrderNumber.localeCompare(b.purchaseOrderNumber)
    );
}

function buildDefaultAllowedReasons(purchaseOrders: PurchaseOrder[]): string[] {
  const warehouseSet = new Set<string>();
  for (const purchaseOrder of purchaseOrders) {
    warehouseSet.add(
      getWarehouseKey(purchaseOrder.division, purchaseOrder.warehouse)
    );
  }

  const allowedReasons: string[] = ["MakeRoom"];
  if (warehouseSet.size <= 1) {
    allowedReasons.unshift("CriticalRisk");
    allowedReasons.push("ModerateRisk");
    return allowedReasons;
  }

  allowedReasons.unshift("BalanceWos");
  return allowedReasons;
}

export function buildIntermediateOrderRedistributionRequest(
  rows: IntermediateOrderSkuRow[] | undefined,
  warehouseDivisions: Record<
    number,
    string
  > = DEFAULT_INTERMEDIATE_ORDER_DIVISIONS,
  rateOfSaleDays = 30,
  poCube = DEFAULT_INTERMEDIATE_ORDER_PO_CUBE,
  algorithm: AlgorithmType = "Milp",
  allowedReasons?: string[],
  avgDaysOfStockBySku: Record<string, number> = {},
  demandSource: DemandSourceType = "InventoryFlow"
): {
  purchaseOrders: PurchaseOrder[];
  rateOfSaleDays: number;
  poCube: number;
  algorithm: AlgorithmType;
  demandSource: DemandSourceType;
  allowedReasons: string[];
  avgDaysOfStockBySku: Record<string, number>;
} {
  const purchaseOrders = buildIntermediateOrderPurchaseOrders(
    rows,
    warehouseDivisions
  );
  const finalAllowedReasons =
    allowedReasons ?? buildDefaultAllowedReasons(purchaseOrders);

  return {
    purchaseOrders,
    rateOfSaleDays,
    poCube,
    algorithm,
    demandSource,
    allowedReasons: finalAllowedReasons,
    avgDaysOfStockBySku,
  };
}
